import { useState } from 'react';
import { ButtonBase, IconButton, Tab, Tabs, Tooltip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import type { Machine } from '../types';
import { type BookingPeriod, getBookingPeriod, getBookingPickupState, isBookingOverdueReturn } from '../constants';
import { DETAIL_PANEL_LABELS as LABELS } from '../../../utils/labels';
import BookingCard from './BookingCard';

const BOOKING_TABS: { key: BookingPeriod; label: string; emptyLabel: string }[] = [
  { key: 'active', label: LABELS.groupActive, emptyLabel: LABELS.noActiveBookings },
  { key: 'future', label: LABELS.groupFuture, emptyLabel: LABELS.noFutureBookings },
  { key: 'past', label: LABELS.groupPast, emptyLabel: LABELS.noPastBookings },
];

interface AllBookingsViewProps {
  machine: Machine;
  pickupHandinEnabled: boolean;
  onBack: () => void;
  onClose: () => void;
  onSelectBooking: (bookingId: string, machineId: string) => void;
}

// Inline "all bookings" sub-view of the machine panel — reached via the
// "Alle boekingen bekijken" button, never a separate dialog or PanelState.
export default function AllBookingsView({
  machine,
  pickupHandinEnabled,
  onBack,
  onClose,
  onSelectBooking,
}: AllBookingsViewProps) {
  const [tabIndex, setTabIndex] = useState(0);

  const activeTab = BOOKING_TABS[tabIndex];
  const tabBookings = (machine.bookings ?? [])
    .filter((bk) => getBookingPeriod(bk, pickupHandinEnabled) === activeTab.key)
    .sort((a, b) =>
      activeTab.key === 'past'
        ? b.end_date.localeCompare(a.end_date)
        : a.start_date.localeCompare(b.start_date)
    );

  function countForTab(key: BookingPeriod): number {
    return (machine.bookings ?? []).filter((bk) => getBookingPeriod(bk, pickupHandinEnabled) === key).length;
  }

  return (
    <>
      <div className="flex items-start gap-2 px-4 py-3 shrink-0 border-b border-border">
        <div className="flex flex-col min-w-0 flex-1">
          <ButtonBase
            onClick={onBack}
            className="flex items-center justify-start gap-1 text-sm text-text-secondary rounded w-full min-w-0"
            data-testid="all-bookings-back"
          >
            <ArrowBackIcon fontSize="inherit" className="shrink-0" />
            <span className="truncate min-w-0">{[machine.name, machine.category].filter(Boolean).join(' · ')}</span>
          </ButtonBase>
          <p className="text-lg font-semibold mb-0 mt-0 truncate">
            {LABELS.allBookingsTitle} ({machine.bookings?.length ?? 0})
          </p>
        </div>
        <Tooltip title={LABELS.close}>
          <IconButton onClick={onClose} color="inherit" aria-label={LABELS.close}>
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <Tabs value={tabIndex} onChange={(_, v: number) => setTabIndex(v)} variant="fullWidth">
          {BOOKING_TABS.map(({ key, label }) => (
            <Tab className="text-xs p-1" key={key} label={`${label} (${countForTab(key)})`} />
          ))}
        </Tabs>

        {tabBookings.length === 0 ? (
          <p className="text-xs italic text-text-secondary">{activeTab.emptyLabel}</p>
        ) : (
          <div className="space-y-2">
            {tabBookings.map((bk) => (
              <BookingCard
                key={bk.id}
                booking={bk}
                isPast={activeTab.key === 'past'}
                showNotes={false}
                pickupState={getBookingPickupState(bk, pickupHandinEnabled)}
                isOverdue={isBookingOverdueReturn(bk, pickupHandinEnabled)}
                onCardClick={(bk) => onSelectBooking(bk.id, machine.id)}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
